import { inferLocaleFromLocalizedPathRoot } from './infer-locale-from-localized-path-root';

export type SupportedLocale = 'en' | 'fi' | 'sv';

/**
 * Resolve route locale from page context + URL.
 *
 * - Physical `/fi/*` and `/sv/*` URLs always win over a wrong `locale: "en"` in context.
 * - `/en/*` with `locale: "fi"|"sv"` trusts context (Vike often exposes logical English paths).
 * - Unprefixed roots that only exist for FI/SV (`tyoelama`, `arbetsliv`, `tyopaikat`, …) win over
 *   `locale: "en"`.
 * - English logical segments (`privacy`, `resources`, …) + `locale: "fi"|"sv"` trust context.
 */
export function resolvePageLocale(input: {
  locale?: string;
  urlPathname?: string;
}): SupportedLocale {
  const fromContext = input.locale;
  const pathname = input.urlPathname;
  const firstSegment = pathname?.split('?')[0].split('/').filter(Boolean)[0]?.toLowerCase();

  if (firstSegment === 'fi' || firstSegment === 'sv') {
    return firstSegment;
  }

  if (firstSegment === 'en') {
    if (fromContext === 'fi' || fromContext === 'sv') {
      return fromContext;
    }
    return 'en';
  }

  const inferredRoot = firstSegment ? inferLocaleFromLocalizedPathRoot(firstSegment) : null;

  // Unprefixed /tyoelama, /arbetsliv, /tyopaikat, … — trust path over wrong context "en"
  if (inferredRoot === 'fi' || inferredRoot === 'sv') {
    return inferredRoot;
  }

  if (fromContext === 'fi' || fromContext === 'sv') {
    return fromContext;
  }

  if (inferredRoot === 'en') {
    return 'en';
  }

  if (fromContext === 'en') {
    return 'en';
  }

  return 'en';
}

/**
 * TEC navbar redesign item 1: the best available pathname for the navbar's active-section
 * highlight (resolveNavActiveSection). `pageContext.urlPathname`/`urlLogical` are a poor source
 * for this: once `web-app/pages/+onBeforeRoute.ts` sets `urlLogical` (it does for every route,
 * including resource paths, where it deliberately keeps the locale-stripped pathname rather than
 * delocalizing it further), Vike's own `urlPathname` getter resolves from `urlLogical` instead of
 * the real request URL (see node_modules/vike/dist/shared-server-client/
 * getPageContextUrlComputed.js's `getUrlParsed`), so for "/en/resources" it silently becomes
 * "/resources". `pageContext.urlOriginal` is the Vike field documented to reflect the actual
 * incoming request URL regardless of what any `onBeforeRoute()` hook sets, so it is preferred
 * here; note that some local dev proxy setups can still hand Vike an already locale-stripped URL
 * before this ever runs, which is why `resolveNavActiveSection` itself also matches the
 * locale-stripped/delocalized shapes directly (see that function's own doc comment) rather than
 * depending solely on getting a locale-prefixed pathname here. Only used for this one purpose:
 * every other pre-existing urlPathname/urlLogical call site in this codebase is untouched, since
 * some of those intentionally want the delocalized/locale-resolution behavior this function does
 * not provide.
 */
export function resolveNavPagePathname(pageContext: { urlOriginal?: string; urlPathname?: string; urlLogical?: string } | null | undefined): string | undefined {
  const raw = pageContext?.urlOriginal ?? pageContext?.urlPathname ?? pageContext?.urlLogical;
  if (!raw) return undefined;
  return raw.split('?')[0]?.split('#')[0];
}
